import { z } from "zod"
import { productVisualPrioritySchema, type ProductVisualPriority } from "./product-visual-dispatch"

export const PIM_TARGET_RECOVERY_LIMIT = 100
export const PIM_TARGET_RECOVERY_CLASSIFICATION = {
  existingSuccess: "existing_success",
  recoveryRequired: "recovery_required",
  activeWork: "active_work",
  unavailable: "unavailable",
} as const

const targetIdSchema = z.number().int().positive().max(2_147_483_647)
export const pimTargetRecoveryIdSchema = z.string().uuid()
export const pimTargetRecoveryPreviewRequestSchema = z
  .object({
    targetIds: z.array(targetIdSchema).min(1).max(PIM_TARGET_RECOVERY_LIMIT),
  })
  .strict()
  .refine((input) => new Set(input.targetIds).size === input.targetIds.length, {
    message: "Select distinct target IDs",
  })
export const pimTargetRecoveryExecuteRequestSchema = z
  .object({
    priority: productVisualPrioritySchema.optional(),
    targets: z
      .array(
        z
          .object({
            targetId: targetIdSchema,
            evidenceHash: z.string().regex(/^[a-f0-9]{64}$/),
          })
          .strict()
      )
      .min(1)
      .max(PIM_TARGET_RECOVERY_LIMIT),
  })
  .strict()
  .refine(
    (input) =>
      new Set(input.targets.map((target) => target.targetId)).size === input.targets.length,
    {
      message: "Select distinct target IDs",
    }
  )

export type PimTargetRecoveryPreviewRequest = z.infer<typeof pimTargetRecoveryPreviewRequestSchema>
export type PimTargetRecoveryExecuteRequest = z.infer<typeof pimTargetRecoveryExecuteRequestSchema>
export type PimTargetRecoveryClassification =
  (typeof PIM_TARGET_RECOVERY_CLASSIFICATION)[keyof typeof PIM_TARGET_RECOVERY_CLASSIFICATION]
export type PimTargetRecoveryPreviewTarget = {
  targetId: number
  eventId: number | null
  store: string | null
  productId: number | null
  simpleSku: string | null
  kind: "nutrition" | "amino" | null
  evidenceHash: string
  classification: PimTargetRecoveryClassification
  blockers: string[]
  recoveryId: string | null
  resolved: boolean
  affectedScopes: { store: string; sku: string; productId: number | null }[]
}
export type PimTargetRecoveryPreview = { targets: PimTargetRecoveryPreviewTarget[] }

export type PimTargetRecoveryFootprint = {
  store: string
  simpleSku: string
  productId: number
  kind: "nutrition" | "amino"
  locale: string
  contentFingerprint: string
  renderFingerprint: string | null
  imageId: number | null
  artifactHash: string | null
}
export type PimTargetRecoveryProof = {
  sourceObservedAt: string
  sourceFingerprint: string
  ownerStore: string
  locale: string
  inputFingerprint: string
  localEvidenceHash: string
  verifiedAt: string
  publishedAt: string
  footprint: PimTargetRecoveryFootprint[]
}
export type PimTargetRecoveryReadback = {
  priority: ProductVisualPriority
  id: string
  targetId: number
  state: "admitted" | "waiting" | "blocked" | "resolved"
  evidenceHash: string
  reservedFillJobId: string
  linkedWork: { fillJobId: string | null; fillJobIds?: string[]; replacementIds: string[] }
  linkedWorkTruncated?: boolean
  blockers: string[]
  originalFailure: {
    eventId: number
    store: string
    productId: number
    simpleSku: string | null
    kind: "nutrition" | "amino"
    cause: string | null
    evidence: string | null
    terminalAt: string | null
  }
  resolution: { resolvedAt: string; evidenceHash: string; proof: PimTargetRecoveryProof } | null
  createdAt: string
  updatedAt: string
}
